/**
 * SchedulePage — construction two-week look-ahead calendar.
 *
 * The page shows either a 14-day (default) or ~30-day grid of tasks with:
 *   • horizontal project pills (colour represents project in All Projects
 *     mode, is a visual anchor in single-project mode)
 *   • horizontal people pills (filters within a single project)
 *   • Add Task modal + click-a-day quick-add
 *   • task edit / delete modal
 *   • archive / restore admin drawer (super admin only)
 *
 * Color logic per the spec:
 *   ALL PROJECTS         → task colour = project colour
 *   SINGLE PROJECT       → task colour = primary assignee colour
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, CaretLeft, CaretRight, DotsThreeVertical, Trash, ArrowCounterClockwise, Warning, CalendarBlank,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMessage } from "@/lib/api";

// ----- date helpers (local-only, no UTC drift) -----
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseYmd(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfWeekMon(d) { const c = new Date(d); const day = (c.getDay() + 6) % 7; c.setDate(c.getDate() - day); c.setHours(0, 0, 0, 0); return c; }
function startOfMonth(d) { const c = new Date(d.getFullYear(), d.getMonth(), 1); return startOfWeekMon(c); }
function fmtDayLabel(d) { return d.toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" }); }
function sameDate(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

// Compute a readable text colour for a given brand hex — used to make task
// bars legible against arbitrary project colours.
function textOn(hex) {
  const s = (hex || "").replace("#", "");
  if (s.length < 6) return "#ffffff";
  const r = parseInt(s.slice(0, 2), 16);
  const g = parseInt(s.slice(2, 4), 16);
  const b = parseInt(s.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155 ? "#0f172a" : "#ffffff";
}

export default function SchedulePage() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const canManageArchive = isSuper;
  const canAddProject = isSuper || isAdmin || user?.role === "view_only"; // employees can add per spec
  const canAddPerson = canAddProject;

  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // View
  const [viewMode, setViewMode] = useState("2w"); // "2w" | "month"
  const [anchorDate, setAnchorDate] = useState(() => startOfWeekMon(new Date()));

  // Filters
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedPerson, setSelectedPerson] = useState("all");

  // Modals
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [taskModal, setTaskModal] = useState(null); // { mode: "create"|"edit", initial: {...}, task?: {...} }
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteProject, setDeleteProject] = useState(null); // project doc

  const rangeStart = useMemo(
    () => viewMode === "2w" ? anchorDate : startOfMonth(anchorDate),
    [viewMode, anchorDate],
  );
  const days = viewMode === "2w" ? 14 : 42; // month view uses a 6-row grid
  const rangeEnd = useMemo(() => addDays(rangeStart, days - 1), [rangeStart, days]);

  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const personById = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);

  // ----- data loaders -----
  const loadProjects = useCallback(async () => {
    const { data } = await api.get("/schedule/projects");
    setProjects(Array.isArray(data) ? data : []);
  }, []);
  const loadPeople = useCallback(async () => {
    const { data } = await api.get("/schedule/people");
    setPeople(Array.isArray(data) ? data : []);
  }, []);
  const loadTasks = useCallback(async () => {
    const params = { start: ymd(rangeStart), end: ymd(rangeEnd) };
    if (selectedProject !== "all") params.project_id = selectedProject;
    if (selectedPerson !== "all") params.person_id = selectedPerson;
    const { data } = await api.get("/schedule/tasks", { params });
    setTasks(Array.isArray(data) ? data : []);
  }, [rangeStart, rangeEnd, selectedProject, selectedPerson]);

  // Initial + on-filter-change load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        await Promise.all([loadProjects(), loadPeople(), loadTasks()]);
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadProjects, loadPeople, loadTasks]);

  // Person filter is not reset when the project filter changes — the
  // caller frequently wants to keep watching "Omar" across every project.

  // ----- range navigation -----
  const goPrev = () => setAnchorDate(addDays(anchorDate, viewMode === "2w" ? -14 : -30));
  const goNext = () => setAnchorDate(addDays(anchorDate, viewMode === "2w" ? 14 : 30));
  const goToday = () => setAnchorDate(startOfWeekMon(new Date()));

  const isAllProjects = selectedProject === "all";

  // Colour rule:
  //   • All Projects (any people filter)                → project colour
  //   • Single Project, All People                       → assignee colour
  //   • Single Project + Single Person (person filter)   → project colour
  // Rationale: when the caller is focused on ONE person, colour is used to
  // differentiate projects; when focused on ONE project across everyone,
  // colour distinguishes assigned people; the "all projects" view always
  // paints by project so the legend stays meaningful.
  const isSinglePerson = selectedPerson !== "all";
  const taskColor = useCallback((t) => {
    const projectColor = projectById[t.project_id]?.color || "#64748b";
    if (isAllProjects || isSinglePerson) return projectColor;
    const pid = (t.assignee_ids || [])[0];
    if (pid && personById[pid]) return personById[pid].color;
    return projectColor;
  }, [isAllProjects, isSinglePerson, projectById, personById]);

  // ----- task quick-open helpers -----
  const openCreate = (dateStr) => {
    setTaskModal({
      mode: "create",
      initial: {
        title: "",
        project_id: isAllProjects ? (projects[0]?.id || "") : selectedProject,
        start_date: dateStr || ymd(new Date()),
        end_date: dateStr || ymd(new Date()),
        description: "",
        assignee_ids: (selectedPerson !== "all") ? [selectedPerson] : [],
      },
    });
  };

  const openEdit = (task) => {
    setTaskModal({
      mode: "edit",
      task,
      initial: {
        title: task.title,
        project_id: task.project_id,
        start_date: task.start_date,
        end_date: task.end_date,
        description: task.description || "",
        assignee_ids: task.assignee_ids || [],
      },
    });
  };

  // ----- task grouping for rendering -----
  const tasksByWeek = useMemo(() => groupIntoWeeks(tasks, rangeStart, days), [tasks, rangeStart, days]);

  return (
    <div className="h-screen bg-[#f5f7fb] flex flex-col overflow-hidden">
      <AppHeader />
      <main className="flex-1 max-w-none w-full mx-auto p-3 sm:p-4 lg:p-5 xl:p-6 space-y-3 overflow-y-auto flex flex-col">
        {/* HEADER */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="schedule-title">Schedule</h1>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-0.5">
              Construction Two-Week Look-Ahead
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuper && (
              <Button
                variant="outline"
                size="sm"
                className="border-slate-300 text-slate-700"
                onClick={() => setArchiveOpen(true)}
                data-testid="schedule-manage-btn"
                title="Manage archived projects & people"
              >
                <DotsThreeVertical size={14} className="mr-1" /> Manage
              </Button>
            )}
            <Button
              onClick={() => openCreate(ymd(new Date()))}
              className="bg-[#1f64c2] hover:bg-[#1854a3]"
              size="sm"
              data-testid="schedule-add-task"
            >
              <Plus size={13} className="mr-1" /> Add Task
            </Button>
          </div>
        </div>

        {/* PROJECT PILLS */}
        <PillRow
          label="Project"
          items={[{ id: "all", name: "All Projects", color: "#64748b" }, ...projects]}
          selected={selectedProject}
          onSelect={setSelectedProject}
          onAdd={canAddProject ? () => setAddProjectOpen(true) : null}
          testIdPrefix="project"
        />

        {/* PEOPLE PILLS — always active; filters tasks by assignee in
             every view. In All-Projects mode picking a person paints
             tasks by project (see taskColor rule). */}
        <PillRow
          label="People"
          items={[{ id: "all", name: "All People", color: "#64748b" }, ...people]}
          selected={selectedPerson}
          onSelect={setSelectedPerson}
          onAdd={canAddPerson ? () => setAddPersonOpen(true) : null}
          testIdPrefix="person"
        />

        {/* CONTROLS ROW */}
        <div className="flex items-center flex-wrap gap-2">
          <div className="inline-flex items-center rounded-md border border-slate-300 overflow-hidden bg-white">
            <button
              onClick={goPrev}
              className="px-2 py-1.5 hover:bg-slate-100"
              data-testid="schedule-prev"
              aria-label="Previous"
            ><CaretLeft size={13} /></button>
            <button
              onClick={goToday}
              className="px-3 py-1.5 text-xs font-semibold border-x border-slate-300 hover:bg-slate-100"
              data-testid="schedule-today"
            >Today</button>
            <button
              onClick={goNext}
              className="px-2 py-1.5 hover:bg-slate-100"
              data-testid="schedule-next"
              aria-label="Next"
            ><CaretRight size={13} /></button>
          </div>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="h-8 border border-slate-300 rounded-md px-2 text-xs bg-white font-semibold"
            data-testid="schedule-view-mode"
          >
            <option value="2w">2 Weeks</option>
            <option value="month">Month</option>
          </select>
          <div className="text-xs text-slate-600 ml-1">
            {fmtDayLabel(rangeStart)} — {fmtDayLabel(rangeEnd)}
          </div>
          {isAllProjects && projects.length > 0 && (
            <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="schedule-legend">
              {projects.slice(0, 8).map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
                  {p.name}
                </span>
              ))}
              {projects.length > 8 && <span className="text-[10px] text-slate-400 italic">+{projects.length - 8} more</span>}
            </div>
          )}
        </div>

        {/* CALENDAR */}
        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
            <Warning size={16} /> {error}
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
            Loading schedule…
          </div>
        ) : (
          <CalendarGrid
            weeks={tasksByWeek}
            rangeStart={rangeStart}
            onDayClick={(d) => openCreate(ymd(d))}
            onTaskClick={openEdit}
            taskColor={taskColor}
            projectById={projectById}
            personById={personById}
            isAllProjects={isAllProjects}
            viewMode={viewMode}
          />
        )}

        {projects.length === 0 && !loading && !error && (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="schedule-empty-projects">
            <CalendarBlank size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="font-semibold text-slate-700">Create your first project to start building your two-week look-ahead.</p>
            {canAddProject && (
              <Button onClick={() => setAddProjectOpen(true)} size="sm" className="mt-3 bg-[#1f64c2] hover:bg-[#1854a3]">
                <Plus size={13} className="mr-1" /> Add project
              </Button>
            )}
          </div>
        )}
      </main>

      {/* MODALS */}
      <AddProjectModal
        open={addProjectOpen}
        onClose={() => setAddProjectOpen(false)}
        onCreated={(p) => { setProjects((prev) => [...prev, p]); setSelectedProject(p.id); }}
      />
      <AddPersonModal
        open={addPersonOpen}
        onClose={() => setAddPersonOpen(false)}
        onCreated={(p) => setPeople((prev) => [...prev, p])}
      />
      <TaskModal
        state={taskModal}
        projects={projects}
        people={people}
        onClose={() => setTaskModal(null)}
        onSaved={loadTasks}
      />
      <ArchiveManageModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onChanged={async () => { await Promise.all([loadProjects(), loadPeople()]); await loadTasks(); }}
      />
      <DeleteProjectConfirm
        project={deleteProject}
        onClose={() => setDeleteProject(null)}
        onDeleted={async () => { setDeleteProject(null); if (selectedProject === deleteProject?.id) setSelectedProject("all"); await Promise.all([loadProjects(), loadTasks()]); }}
      />
    </div>
  );
}

// ============================================================
// PILL ROW
// ============================================================
function PillRow({ label, items, selected, onSelect, onAdd, disabled, testIdPrefix }) {
  const scrollRef = useRef(null);
  return (
    <div className={`flex items-center gap-2 ${disabled ? "opacity-60" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 w-14 shrink-0">{label}</div>
      <div ref={scrollRef} className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0" data-testid={`${testIdPrefix}-pills`}>
        {items.map((it) => {
          const active = String(selected) === String(it.id);
          return (
            <button
              key={it.id}
              onClick={() => !disabled && onSelect(it.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#1f64c2] text-white border-[#1f64c2]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`${testIdPrefix}-pill-${it.id}`}
              disabled={disabled}
            >
              {it.color && it.id !== "all" && (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: it.color }} />
              )}
              {it.name}
            </button>
          );
        })}
        {onAdd && (
          <button
            onClick={onAdd}
            disabled={disabled}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-dashed border-[#1f64c2] text-[#1f64c2] hover:bg-[#eaf2ff]"
            data-testid={`${testIdPrefix}-pill-add`}
          >
            <Plus size={12} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// CALENDAR GRID — 7-column, N-row layout with multi-day bars.
// ============================================================
function groupIntoWeeks(tasks, rangeStart, days) {
  // Break the range into weeks of 7 days. For each week, compute the
  // task "lanes" (rows) so multi-day bars never overlap.
  const weeks = [];
  const numWeeks = Math.ceil(days / 7);
  for (let w = 0; w < numWeeks; w++) {
    const weekStart = addDays(rangeStart, w * 7);
    const weekEnd = addDays(weekStart, 6);
    const weekStartStr = ymd(weekStart);
    const weekEndStr = ymd(weekEnd);
    // Tasks that overlap this week.
    const overlapping = tasks
      .filter((t) => t.start_date <= weekEndStr && t.end_date >= weekStartStr)
      .map((t) => {
        const start = t.start_date < weekStartStr ? weekStart : parseYmd(t.start_date);
        const end = t.end_date > weekEndStr ? weekEnd : parseYmd(t.end_date);
        const startCol = Math.floor((start - weekStart) / 86400000);
        const endCol = Math.floor((end - weekStart) / 86400000);
        return { ...t, _startCol: Math.max(0, startCol), _endCol: Math.min(6, endCol) };
      })
      .sort((a, b) => a._startCol - b._startCol || (a._endCol - a._startCol) - (b._endCol - b._startCol));
    // Assign lanes (each lane is a row within the week — a task cannot
    // share a lane with another task whose columns overlap).
    const lanes = [];
    for (const t of overlapping) {
      let placed = false;
      for (const lane of lanes) {
        if (lane.every((x) => x._endCol < t._startCol || x._startCol > t._endCol)) {
          lane.push(t);
          placed = true;
          break;
        }
      }
      if (!placed) lanes.push([t]);
    }
    weeks.push({ weekStart, weekEnd, lanes });
  }
  return weeks;
}

function CalendarGrid({ weeks, rangeStart, onDayClick, onTaskClick, taskColor, projectById, personById, isAllProjects, viewMode }) {
  const today = new Date();
  // In the 2-week view the calendar consumes the full viewport height so
  // callers can see every task per day without cramping; the month view
  // spreads 6 weeks over the same space so each row is shorter.
  const HEADER_OFFSET_PX = viewMode === "2w" ? 320 : 340;
  const MIN_ROW_2W = 240;
  const MIN_ROW_M = 130;
  const LANE_HEIGHT = 20;
  const DAY_LABEL = 30;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col"
      style={{ minHeight: `calc(100vh - ${HEADER_OFFSET_PX}px)` }}
    >
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 text-center py-2">{d}</div>
        ))}
      </div>
      <div className="flex-1 flex flex-col">
        {weeks.map((wk, wi) => {
          const laneCount = Math.max(1, wk.lanes.length);
          const contentHeight = DAY_LABEL + laneCount * (LANE_HEIGHT + 2);
          const rowMin = viewMode === "2w" ? Math.max(MIN_ROW_2W, contentHeight) : Math.max(MIN_ROW_M, contentHeight);
          return (
            <div key={wi} className="border-b border-slate-200 last:border-b-0 flex-1 flex flex-col" style={{ minHeight: rowMin }}>
              <div className="grid grid-cols-7 relative flex-1">
                {/* Day cells (backgrounds + click handler + day-number labels) */}
                {Array.from({ length: 7 }).map((_, ci) => {
                  const d = addDays(wk.weekStart, ci);
                  const isToday = sameDate(d, today);
                  const isWeekend = ci >= 5;
                  return (
                    <button
                      type="button"
                      key={ci}
                      onClick={() => onDayClick(d)}
                      className={`text-left px-2 pt-1.5 border-r border-slate-100 last:border-r-0 hover:bg-slate-50 transition-colors ${isWeekend ? "bg-slate-50/40" : ""} ${isToday ? "bg-[#eaf2ff]/40" : ""}`}
                      data-testid={`day-cell-${ymd(d)}`}
                    >
                      <div className={`inline-flex items-center gap-1.5 ${isToday ? "text-[#1f64c2] font-bold" : "text-slate-700"} text-xs`}>
                        <span className={isToday ? "bg-[#1f64c2] text-white rounded-full w-5 h-5 inline-flex items-center justify-center text-[10px] font-bold" : ""}>{d.getDate()}</span>
                        {isToday && <span className="text-[9px] uppercase tracking-widest font-bold">Today</span>}
                      </div>
                    </button>
                  );
                })}
                {/* Task bars overlay */}
                {wk.lanes.map((lane, li) => (
                  lane.map((t) => {
                    const bg = taskColor(t);
                    const fg = textOn(bg);
                    const left = `calc(${(t._startCol / 7) * 100}% + 3px)`;
                    const width = `calc(${((t._endCol - t._startCol + 1) / 7) * 100}% - 6px)`;
                    const top = DAY_LABEL + li * (LANE_HEIGHT + 2);
                    const proj = projectById[t.project_id];
                    const primary = personById[(t.assignee_ids || [])[0]];
                    const extra = (t.assignee_ids || []).length - 1;
                    return (
                      <div
                        key={t.id + "-" + wi}
                        onClick={(e) => { e.stopPropagation(); onTaskClick(t); }}
                        className="absolute rounded text-[11px] font-semibold px-1.5 leading-none flex items-center cursor-pointer shadow-sm truncate hover:brightness-95"
                        style={{ left, width, top, height: LANE_HEIGHT, backgroundColor: bg, color: fg }}
                        title={`${t.title} · ${proj?.name || ""}${primary ? " · " + primary.name : ""}`}
                        data-testid={`task-bar-${t.id}`}
                      >
                        <span className="truncate">{t.title}</span>
                        {primary && <span className="opacity-80 ml-1"> · {primary.name}</span>}
                        {extra > 0 && <span className="opacity-80 ml-1"> +{extra}</span>}
                        {isAllProjects && proj && <span className="opacity-80 ml-1"> · {proj.name}</span>}
                      </div>
                    );
                  })
                ))}
                {wk.lanes.length === 0 && (
                  <div className="absolute inset-x-0 top-9 text-center text-[10px] text-slate-300 italic pointer-events-none">
                    No scheduled tasks
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// ADD PROJECT MODAL
// ============================================================
function AddProjectModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setName(""); setColor(""); } }, [open]);
  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/schedule/projects", { name: n, color: color || null });
      toast.success(`Project "${data.name}" added`);
      onCreated(data);
      onClose();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="schedule-add-project-modal">
        <DialogHeader><DialogTitle>Add project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Project name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 1 Carnation" autoFocus data-testid="schedule-project-name" />
          </label>
          <ColorField label="Color (optional)" value={color} onChange={setColor} testId="schedule-project-color" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="schedule-project-save">
            {saving ? "Adding…" : "Add project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ADD PERSON MODAL
// ============================================================
function AddPersonModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setName(""); setColor(""); } }, [open]);
  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/schedule/people", { name: n, color: color || null });
      toast.success(`"${data.name}" added`);
      onCreated(data);
      onClose();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="schedule-add-person-modal">
        <DialogHeader><DialogTitle>Add person / crew</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Omar, ABC Plumbing, Painter" autoFocus data-testid="schedule-person-name" />
            <span className="text-[10px] text-slate-500 italic">This does not have to be a STRATA login user — crews, subs and vendors are fine.</span>
          </label>
          <ColorField label="Color (optional)" value={color} onChange={setColor} testId="schedule-person-color" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="schedule-person-save">
            {saving ? "Adding…" : "Add person"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColorField({ label, value, onChange, testId }) {
  const suggested = ["#2563EB", "#16A34A", "#F97316", "#DB2777", "#9333EA", "#0891B2", "#DC2626", "#059669"];
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="flex flex-wrap items-center gap-2">
        {suggested.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={`w-6 h-6 rounded-full ring-2 transition-all ${value === c ? "ring-[#1f64c2] scale-110" : "ring-transparent hover:ring-slate-300"}`}
            style={{ backgroundColor: c }}
            data-testid={`${testId}-swatch-${c.replace('#', '')}`}
          />
        ))}
        <input type="color" value={value || "#1f64c2"} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 border border-slate-300 rounded cursor-pointer" data-testid={`${testId}-custom`} />
        {value ? (
          <button type="button" onClick={() => onChange("")} className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-800">Auto</button>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-slate-400 italic">Auto-assigned</span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TASK MODAL — create + edit
// ============================================================
function TaskModal({ state, projects, people, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const canDelete = !!user; // authenticated users can delete tasks per spec

  useEffect(() => { setForm(state ? { ...state.initial } : null); }, [state]);
  if (!state || !form) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAssignee = (id) => setForm((f) => {
    const set = new Set(f.assignee_ids || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...f, assignee_ids: Array.from(set) };
  });

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Task title is required"); return; }
    if (!form.project_id) { toast.error("Please choose a project"); return; }
    if (form.end_date < form.start_date) { toast.error("End date must be on or after start date"); return; }
    setSaving(true);
    try {
      if (state.mode === "create") {
        await api.post("/schedule/tasks", {
          title: form.title.trim(),
          project_id: form.project_id,
          start_date: form.start_date,
          end_date: form.end_date,
          description: form.description,
          assignee_ids: form.assignee_ids,
        });
        toast.success("Task added");
      } else {
        await api.patch(`/schedule/tasks/${state.task.id}`, {
          title: form.title.trim(),
          project_id: form.project_id,
          start_date: form.start_date,
          end_date: form.end_date,
          description: form.description,
          assignee_ids: form.assignee_ids,
        });
        toast.success("Task updated");
      }
      await onSaved();
      onClose();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${state.task.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/schedule/tasks/${state.task.id}`);
      toast.success("Task deleted");
      await onSaved();
      onClose();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="schedule-task-modal">
        <DialogHeader>
          <DialogTitle>{state.mode === "create" ? "Add task" : "Edit task"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Task title</span>
            <Input value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="e.g. Install kitchen cabinets" autoFocus data-testid="schedule-task-title" />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-[11px]">
              <span className="uppercase tracking-widest font-semibold text-slate-500">Project</span>
              <select value={form.project_id} onChange={(e) => setField("project_id", e.target.value)} className="w-full mt-1 h-9 border border-slate-300 rounded-md px-2 bg-white text-sm" data-testid="schedule-task-project">
                <option value="">— Choose —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <div />
            <label className="block text-[11px]">
              <span className="uppercase tracking-widest font-semibold text-slate-500">Start date</span>
              <Input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} data-testid="schedule-task-start" />
            </label>
            <label className="block text-[11px]">
              <span className="uppercase tracking-widest font-semibold text-slate-500">End date</span>
              <Input type="date" value={form.end_date} onChange={(e) => setField("end_date", e.target.value)} data-testid="schedule-task-end" />
            </label>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Assigned to</div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {people.length === 0 && <div className="text-[11px] text-slate-400 italic">No people added yet.</div>}
              {people.map((p) => {
                const on = (form.assignee_ids || []).includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleAssignee(p.id)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "text-white border-transparent" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                    style={on ? { backgroundColor: p.color } : {}}
                    data-testid={`schedule-task-assignee-${p.id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Notes (optional)</span>
            <Textarea rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Coordinate delivery before installation…" data-testid="schedule-task-notes" />
          </label>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {state.mode === "edit" && canDelete && (
            <Button variant="outline" onClick={del} disabled={deleting} className="text-rose-600 border-rose-200 hover:bg-rose-50 mr-auto" data-testid="schedule-task-delete">
              <Trash size={13} className="mr-1" /> {deleting ? "Deleting…" : "Delete task"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="schedule-task-save">
            {saving ? "Saving…" : state.mode === "create" ? "Add task" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// DELETE PROJECT CONFIRM (super only)
// ============================================================
function DeleteProjectConfirm({ project, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!project) return null;
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/schedule/projects/${project.id}`);
      toast.success(`Archived "${project.name}"`);
      await onDeleted();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <Warning size={18} weight="fill" /> Delete Project?
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-700 space-y-2">
          <p>Are you sure you want to remove <strong>{project.name}</strong> from Schedule? The project and its schedule data will be archived and can be restored later.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={del} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="schedule-delete-project-confirm">
            {busy ? "Deleting…" : "Delete Project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ARCHIVE MANAGE (super) — archive/restore projects + people
// ============================================================
function ArchiveManageModal({ open, onClose, onChanged }) {
  const [tab, setTab] = useState("projects");
  const [projectsAll, setProjectsAll] = useState([]);
  const [peopleAll, setPeopleAll] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [p, ppl] = await Promise.all([
      api.get("/schedule/projects", { params: { scope: "all" } }),
      api.get("/schedule/people", { params: { scope: "all" } }),
    ]);
    setProjectsAll(p.data || []);
    setPeopleAll(ppl.data || []);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const act = async (kind, id, action) => {
    setBusy(id + action);
    try {
      const base = kind === "project" ? "projects" : "people";
      if (action === "archive") await api.delete(`/schedule/${base}/${id}`);
      else await api.post(`/schedule/${base}/${id}/restore`);
      toast.success(action === "archive" ? "Archived" : "Restored");
      await load(); await onChanged();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setBusy(""); }
  };

  const rows = tab === "projects" ? projectsAll : peopleAll;
  const kind = tab === "projects" ? "project" : "person";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="schedule-manage-modal">
        <DialogHeader><DialogTitle>Manage schedule</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1 border-b border-slate-200 mb-2">
          {["projects", "people"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px capitalize ${tab === t ? "border-[#1f64c2] text-[#1f64c2]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`schedule-manage-tab-${t}`}
            >{t}</button>
          ))}
        </div>
        <div className="max-h-[400px] overflow-y-auto space-y-1.5">
          {rows.length === 0 && <div className="text-[11px] text-slate-400 italic p-4 text-center">Nothing here yet.</div>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 border border-slate-200 rounded-md p-2" data-testid={`schedule-manage-row-${r.id}`}>
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{r.name}</div>
                {r.deleted ? (
                  <div className="text-[10px] text-rose-500 uppercase tracking-widest font-bold">Archived{r.deleted_by_name ? ` · by ${r.deleted_by_name}` : ""}</div>
                ) : (
                  <div className="text-[10px] text-emerald-600 uppercase tracking-widest font-bold">Active</div>
                )}
              </div>
              {r.deleted ? (
                <Button size="sm" onClick={() => act(kind, r.id, "restore")} disabled={busy === r.id + "restore"} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`schedule-manage-restore-${r.id}`}>
                  <ArrowCounterClockwise size={12} className="mr-1" /> Restore
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => act(kind, r.id, "archive")} disabled={busy === r.id + "archive"} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`schedule-manage-archive-${r.id}`}>
                  <Trash size={12} className="mr-1" /> Archive
                </Button>
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
